/**
 * AI API Client
 * ──────────────
 * Typed wrapper for /api/ai/* endpoints.
 */
import { apiClient } from './api';

export interface AdvisoryResult {
  title: string;
  content: string;
  category: string;
}

export interface MELInsight {
  insights: string[];
  recommendations: string[];
  riskLevel: 'low' | 'moderate' | 'high';
}

export interface PestScanResult {
  diagnosis: string;
  confidence: number;
  severity: 'low' | 'moderate' | 'high' | 'critical';
  description: string;
  recommendations: string;
  reportId: string;
  savedAt: string;
}

export interface PestReport {
  id: string;
  farmer_id: string;
  farmer_name: string;
  farmer_village: string;
  diagnosis: string;
  confidence: number;
  severity: string;
  description: string;
  recommendations: string;
  status: string;
  admin_notes: string | null;
  created_at: string;
  reviewed_at: string | null;
}

export interface PestStats {
  total: number;
  last7d: number;
  byDiagnosis: Array<{ diagnosis: string; count: number }>;
  bySeverity: Array<{ severity: string; count: number }>;
  byStatus: Array<{ status: string; count: number }>;
}

export const aiApi = {
  generateAdvisory: async (topic: string, language?: string): Promise<AdvisoryResult> => {
    const res = await apiClient.post('/ai/advisory/generate', { topic, language });
    return res.data;
  },

  analyzeMEL: async (metrics: any): Promise<MELInsight> => {
    const res = await apiClient.post('/ai/mel/analyze', { metrics });
    return res.data;
  },

  scanPest: async (photoBase64: string, batchId?: string): Promise<PestScanResult> => {
    const res = await apiClient.post('/ai/pest/scan', { photoBase64, batchId });
    return res.data;
  },

  getPestReports: async (limit = 100, status?: string): Promise<{ reports: PestReport[]; count: number }> => {
    const params: any = { limit };
    if (status) params.status = status;
    const res = await apiClient.get('/ai/pest/reports', { params });
    return res.data;
  },

  getMyPestReports: async (): Promise<{ reports: PestReport[]; count: number }> => {
    const res = await apiClient.get('/ai/pest/my-reports');
    return res.data;
  },

  updatePestReport: async (id: string, data: { status?: string; adminNotes?: string }): Promise<any> => {
    const res = await apiClient.patch(`/ai/pest/reports/${id}`, data);
    return res.data;
  },

  getPestStats: async (): Promise<PestStats> => {
    const res = await apiClient.get('/ai/pest/stats');
    return res.data;
  },
};

export default aiApi;
